import FileSize from "../../../../components/commonComponent/FileSize";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import copyrightPolicyPdf from "../../../../assets/policy_pdf/Copyright Policy.pdf";
import cmapPolicyPdf from "../../../../assets/policy_pdf/Content Contribution, Moderation and Approval (CMAP) policy.pdf";
import archivalPolicyPdf from "../../../../assets/policy_pdf/Content Archival (CAP) policy.pdf";
import reviewPolicyPdf from "../../../../assets/policy_pdf/Content Review (CRP) policy.pdf";
import hyperlinkingPolicyPdf from "../../../../assets/policy_pdf/Hyperlinking Policy.pdf";
import privacyPolicyPdf from "../../../../assets/policy_pdf/Privacy Policy.pdf";
import termsPdf from "../../../../assets/policy_pdf/Terms & Conditions.pdf";
import monitoringPlanPdf from "../../../../assets/policy_pdf/Website monitoring plan.pdf";
import contingencyPlanPdf from "../../../../assets/policy_pdf/Contingency Management plan.pdf";
import securityPolicyPdf from "../../../../assets/policy_pdf/Security Policy.pdf";

const policies = [
  { label: "Copyright Policy", href: copyrightPolicyPdf, fileName: "Copyright Policy.pdf" },
  { label: "Content Contribution, Moderation and Approval (CMAP) Policy", href: cmapPolicyPdf, fileName: "Content Contribution, Moderation and Approval (CMAP) policy.pdf" },
  { label: "Content Archival (CAP) Policy", href: archivalPolicyPdf, fileName: "Content Archival (CAP) policy.pdf" },
  { label: "Content Review (CRP) Policy", href: reviewPolicyPdf, fileName: "Content Review (CRP) policy.pdf" },
  { label: "Hyper Linking Policy", href: hyperlinkingPolicyPdf, fileName: "Hyperlinking Policy.pdf" },
  { label: "Privacy Policy", href: privacyPolicyPdf, fileName: "Privacy Policy.pdf" },
  { label: "Terms & Conditions", href: termsPdf, fileName: "Terms & Conditions.pdf" },
  { label: "Website Monitoring Plan", href: monitoringPlanPdf, fileName: "Website monitoring plan.pdf" },
  { label: "Contingency Management Plan", href: contingencyPlanPdf, fileName: "Contingency Management plan.pdf" },
  { label: "Security Policy", href: securityPolicyPdf, fileName: "Security Policy.pdf" },
];

const Wrapper = styled.div`
  position: relative;
  text-align: left;

  > button {
    color: white;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 14px;
    border: 1px solid transparent;
    border-radius: 999px;
    font: inherit;
    background: transparent;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;
  }

  > button:hover,
  > button:focus-visible,
  > button[aria-expanded="true"] {
    border-color: rgba(255, 255, 255, 0.68);
    background: rgba(255, 255, 255, 0.08);
  }

  button:focus-visible {
    outline: 2px solid #f3a90d;
    outline-offset: 3px;
  }
`;

const Card = styled.div`
  position: absolute;
  z-index: 40;
  bottom: 100%;
  right: -90px;
  width: min(380px, calc(100vw - 48px));
  padding-bottom: 12px;

  &::after {
    content: "";
    position: absolute;
    bottom: 4px;
    right: 106px;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid #365680;
  }

  > div {
    position: relative;
    background: #365680;
    color: #ffffff;
    border-radius: 14px;
    padding: 18px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
    max-height: min(560px, 70vh);
    overflow-y: auto;
  }

  h3 {
    width: fit-content;
    margin: 0 0 12px;
    font-size: 18px;
    font-weight: 600;
  }
  > div::before {
    content: "";
    position: absolute;
    top: 12px;
    left: 0;
    width: 4px;
    height: 42px;
    background: #f3a90d;
    border-radius: 999px;
    pointer-events: none;
  }
  ul { list-style: none; margin: 0; padding: 0; }
  li + li { border-top: 1px solid rgba(255, 255, 255, 0.2); }
  li button {
    display: block;
    width: 100%;
    padding: 10px 8px;
    text-align: left;
    font-size: 14px;
    line-height: 1.4;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: inherit;
    cursor: pointer;
  }
  li button:hover:not(:disabled) { background: rgba(255, 255, 255, 0.14); color: #ffffff; }
  li button:disabled { cursor: default; color: #e3edf6; }
  small { display: block; font-size: 11px; color: #c9dce9; margin-top: 3px; }

  @media (max-width: 560px) {
    right: auto;
    left: 50%;
    transform: translateX(-50%);
    &::after { right: auto; left: calc(50% - 8px); }
  }
`;

export default function PoliciesMenu({ onSelect }) {
  const [open, setOpen] = useState(false);
  const trigger = useRef(null);
  const wrapper = useRef(null);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event) => {
      if (!wrapper.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [open]);

  return (
    <Wrapper
      ref={wrapper}
      onMouseEnter={() => setOpen(true)}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          trigger.current.focus();
          setOpen(false);
        }
      }}
    >
      <button
        ref={trigger}
        type="button"
        aria-expanded={open}
        aria-controls="footer-policies"
        onFocus={(event) => {
          if (event.currentTarget.matches(":focus-visible")) setOpen(true);
        }}
        onClick={() => setOpen((value) => !value)}
      >
        Policies
        <svg
          aria-hidden="true"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 160ms ease",
          }}
        >
          <path d="m6 15 6-6 6 6" />
        </svg>
      </button>
      {open && (
        <Card id="footer-policies" role="region" aria-label="Website policies">
          <div>
            <h3>Website Policies</h3>
            <ul>
              {policies.map((policy) => (
                <li key={policy.label}>
                  <button
                    type="button"
                    disabled={!policy.href}
                    onClick={() => {
                      onSelect({ title: policy.label, href: policy.href, fileName: policy.fileName });
                      setOpen(false);
                    }}
                  >
                    {policy.label}<FileSize href={policy.href} fileName={policy.fileName} />
                    {!policy.href && <small>PDF coming soon</small>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </Card>
      )}
    </Wrapper>
  );
}
